import { create } from 'zustand';
import type { AggregatedTaskState } from '@/core/models/processingTask';

interface PendingTaskState {
  tasks: Record<string, AggregatedTaskState>;
  upsert: (state: AggregatedTaskState) => void;
  get: (taskId: string) => AggregatedTaskState | undefined;
  remove: (taskId: string) => void;
  hydrate: () => void;
}

const STORAGE_KEY = 'mf:pending-tasks:v1';
/** Drop tasks older than this on hydrate so failed/abandoned ones don't pile up. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface Persisted {
  tasks: Record<string, AggregatedTaskState>;
  savedAt: number;
}

function loadPersisted(): Record<string, AggregatedTaskState> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Persisted;
    if (!parsed || typeof parsed !== 'object') return {};
    if (Date.now() - (parsed.savedAt ?? 0) > MAX_AGE_MS) return {};
    const out: Record<string, AggregatedTaskState> = {};
    for (const [k, v] of Object.entries(parsed.tasks ?? {})) {
      // skip terminal tasks on hydrate — they belong in the note store now
      if (v && v.status !== 'completed') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function savePersisted(tasks: Record<string, AggregatedTaskState>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const payload: Persisted = { tasks, savedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / serialization errors are non-fatal */
  }
}

export const usePendingTaskStore = create<PendingTaskState>((set, get) => ({
  tasks: {},

  upsert: (state) => {
    if (!state.task_id) return;
    set((prev) => {
      const tasks = { ...prev.tasks, [state.task_id]: state };
      savePersisted(tasks);
      return { tasks };
    });
  },

  get: (taskId) => get().tasks[taskId],

  remove: (taskId) => {
    set((prev) => {
      const copy = { ...prev.tasks };
      delete copy[taskId];
      savePersisted(copy);
      return { tasks: copy };
    });
  },

  hydrate: () => {
    const tasks = loadPersisted();
    set({ tasks });
  },
}));
